import React, { useState, useCallback } from 'react';
import { FormControl, TextField, CircularProgress } from '@material-ui/core';

import { useDispatch } from 'react-redux';

import { prosjektActions } from '../../store/prosjektSlice';
import { useGetUsersByProsjektRoleAndRegionQuery } from '../../services/rtkApi';
import { Block } from '@klp/design/Block';
import { ActionButton } from '@klp/design/Action';
import { useKlpStyles } from '@klp/design/Theme/useKlpStyles';
import { TildelKommentarField } from '../TildelKommentar/TildelKommentarField';
import { useTildelKommentar } from '../TildelKommentar/hooks/useTildelKommentar';
import { useAssignResponsibleUser } from './hooks/useAssignResponsibleUser';
import { Autocomplete } from '@material-ui/lab';
import { useTranslation } from 'react-i18next';
import type { TildelAnsvarligProps } from './TildelAnsvarligProps';
import { getBrukerDisplayName } from '@klp/user/utils';

const styles = () => ({
    buttonContainer: {
        display: 'flex',
        justifyContent: 'flex-end',
    },
});

export const TildelAnsvarligSelector = ({
    prosjektId,
    prosessInstansId,
    kandidatGruppe,
    currentAnsvarlig,
    redirectTo,
    activity,
    endreProsessAnsvarlig = true,
    region,
}: TildelAnsvarligProps) => {
    const { t } = useTranslation();
    const classes = useKlpStyles({}, styles);
    const [selectedUserId, setSelectedUserId] = useState<string | null>(
        currentAnsvarlig,
    );
    const [currentComment, setCurrentComment] = useState<string>('');

    const { data: users, isLoading: usersLoading } =
        useGetUsersByProsjektRoleAndRegionQuery(
            {
                rolle: kandidatGruppe,
                region: region ?? undefined,
            },
            { skip: !kandidatGruppe },
        );

    const dispatch = useDispatch();
    const { setActiveModalProsessInstansId } = prosjektActions;

    const handleClose = useCallback(() => {
        dispatch(setActiveModalProsessInstansId(undefined));
    }, [dispatch, setActiveModalProsessInstansId]);

    const {
        isTildelKommentarerFieldInTheForm,
        maxCommentLength,
        attachComment,
    } = useTildelKommentar(activity);
    const { handleAssignResponsibleUser, assigningTask } =
        useAssignResponsibleUser(prosjektId, prosessInstansId, attachComment);

    const handleCommentChange = useCallback(
        (comment: string) => {
            const remaining = maxCommentLength - comment.length;
            if (remaining < 0) return;
            setCurrentComment(comment);
        },
        [maxCommentLength],
    );

    const handleSubmit = useCallback(async () => {
        if (!selectedUserId || !prosjektId) return;

        handleAssignResponsibleUser(
            selectedUserId,
            currentComment,
            redirectTo,
            endreProsessAnsvarlig,
        );

        handleClose();
    }, [
        selectedUserId,
        prosjektId,
        handleAssignResponsibleUser,
        currentComment,
        redirectTo,
        handleClose,
        endreProsessAnsvarlig,
    ]);

    const autocompleteValue =
        users?.find((u) => u.brukerId === selectedUserId) ?? null;

    return (
        <Block marginBottom="compact" marginTop="tiny">
            <FormControl fullWidth={true}>
                <Autocomplete
                    id="assign-responsible-user-autocomplete"
                    options={users ?? []}
                    getOptionLabel={(option) =>
                        getBrukerDisplayName(
                            option.fulltnavn,
                            option.epost,
                            option.brukerId,
                        )
                    }
                    value={autocompleteValue}
                    onChange={(_event, newValue) => {
                        setSelectedUserId(newValue?.brukerId ?? null);
                    }}
                    loading={usersLoading}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label={t('formGenerator.assign.assignTo')}
                            variant="outlined"
                            required={true}
                            error={selectedUserId === null}
                            InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                    <>
                                        {usersLoading && (
                                            <CircularProgress
                                                size={20}
                                                color="inherit"
                                            />
                                        )}
                                        {params.InputProps.endAdornment}
                                    </>
                                ),
                            }}
                            inputProps={{
                                ...params.inputProps,
                                'data-testid': 'assign-responsible-user-input',
                            }}
                        />
                    )}
                />
                {activity && isTildelKommentarerFieldInTheForm && (
                    <Block marginTop="bulky">
                        <TildelKommentarField
                            activity={activity}
                            value={currentComment}
                            onChange={handleCommentChange}
                            maxLength={maxCommentLength}
                        />
                    </Block>
                )}
            </FormControl>

            <Block marginTop="bulky" className={classes.buttonContainer}>
                <ActionButton
                    isLoading={assigningTask}
                    actionType="forward"
                    onClick={handleSubmit}
                    translation="prosjekt.table.tildelAnsvarlig.tildel"
                />
            </Block>
        </Block>
    );
};
